import { Component, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatIconModule } from "@angular/material/icon";

@Component({
  selector: "app-measure-calculation-details-prod",
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatIconModule],
  template: `
    <div class="aggregate-message" *ngIf="!hasCountData && aggregateMessage">
      <mat-icon>info_outline</mat-icon>
      <span>{{ aggregateMessage }}</span>
    </div>

    <div class="measure-calculation-compact" *ngIf="hasCountData">
      <div class="calculation-grid">
        <div class="calc-box" *ngIf="ipp !== null">
          <div class="calc-value ipp-value">{{ ipp | number }}</div>
          <div class="calc-label">IPP</div>
        </div>

        <div class="calc-box" *ngIf="denomExclusion !== null">
          <div class="calc-value exclusion-value">
            {{ denomExclusion | number }}
          </div>
          <div class="calc-label">Exclusions</div>
        </div>

        <div class="calc-box" *ngIf="denomException !== null">
          <div class="calc-value excep-value">
            {{ denomException | number }}
          </div>
          <div class="calc-label">Exceptions</div>
        </div>

        <div class="calc-box" *ngIf="denominator !== null">
          <div class="calc-value denom-value">{{ denominator | number }}</div>
          <div class="calc-label">Denominator</div>
        </div>

        <div class="calc-box" *ngIf="numerator !== null">
          <div class="calc-value numer-value">
            {{ numerator | number }}
          </div>
          <div class="calc-label">Numerator</div>
        </div>
      </div>

      <div class="calculation-formula" *ngIf="denominator !== null && numerator !== null">
        <div class="formula-row">
          <span class="formula-label">Adjusted Denominator:</span>
          <span class="formula-value">
            {{ denominator | number }}
          </span>
        </div>
        <div class="formula-row">
          <span class="formula-label">Performance:</span>
          <span class="formula-value">
            {{ numerator | number }} ÷ {{ denominator | number }} ×
            100% = {{ formattedPerformance }}%
          </span>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .aggregate-message {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 12px;
        margin-bottom: 16px;
        background-color: #f5f5f5;
        border-radius: 4px;
        font-size: 13px;
        color: #555;
      }

      .aggregate-message mat-icon {
        font-size: 18px;
        height: 18px;
        width: 18px;
        flex-shrink: 0;
        color: #888;
        margin-top: 1px;
      }

      .calculation-grid {
        display: flex;
        flex-direction: row;
        box-shadow: 0px 4px 8px 0px #00000026;
      }

      .formula-label {
        font-weight: 500;
        color: #333;
        padding-right: 8px;
      }

      .calculation-details-container {
        padding: 16px;
      }

      .calculation-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
      }

      .header-title {
        font-weight: 500;
        margin: 0;
        color: #333;
      }

      .performance-display {
        font-size: 12px;
        padding: 4px 8px;
        background-color: #e1f5fe;
        color: #03a9f4;
        border-radius: 4px;
      }

      .calculation-boxes {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;
      }

      .calc-box {
        flex: 1;
        padding: 6px;
        background-color: #f5f5f5;
        border-radius: 4px;
        text-align: center;
      }

      .calc-value {
        font-size: 20px;
        font-weight: 500;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
      }

      .calc-label {
        font-size: 12px;
        color: #666;
      }

      .ipp-value {
        color: #2196f3;
      }

      .exclusion-value {
        color: #dc3545;
      }

      .denom-value {
        color: #4caf50;
      }

      .excep-value {
        color: #ff9800;
      }

      .numer-value {
        color: #9c27b0;
      }

      .calculation-formula {
        padding-top: 12px;
        border-top: 1px solid #e0e0e0;
        font-size: 13px;
        color: #666;
      }

      .formula-row {
        text-align: right;
        justify-content: space-between;
        margin-bottom: 8px;
      }
    `,
  ],
})
export class MeasureCalculationDetailsProdComponent {
  @Input() ipp: number | null = null;
  @Input() denominator: number | null = null;
  @Input() denomExclusion: number | null = null;
  @Input() denomException: number | null = null;
  @Input() numerator: number | null = null;
  @Input() performance: number = 0;
  @Input() performanceType: string | null = null;

  get hasCountData(): boolean {
    return (
      this.ipp !== null ||
      this.denominator !== null ||
      this.denomExclusion !== null ||
      this.denomException !== null ||
      this.numerator !== null
    );
  }

  get aggregateMessage(): string | null {
    if (this.hasCountData) return null;
    if (this.performanceType === 'S') {
      return 'This strata is a simple average, calculated by combining the performance of all strata and dividing by the total number of strata.';
    }
    if (this.performanceType === 'W') {
      return 'This strata is a weighted average, calculated by summing the numerators and denominators across all strata.';
    }
    return null;
  }

  get formattedPerformance(): string {
    return (this.performance * 100).toFixed(2);
  }
}